import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { SettingsPayload, Theme } from '../api/types';

const DEFAULTS: SettingsPayload = {
  theme: 'system',
  auto_remove_notes: true,
  highlight_peers: true,
  highlight_same_number: true,
  mistake_mode: 'highlight',
  hints_enabled: true,
};

const LOCAL_KEY = 'sudoku:settings';

interface SettingsCtx {
  settings: SettingsPayload;
  update: (patch: Partial<SettingsPayload>) => void;
}

const Ctx = createContext<SettingsCtx | null>(null);

function loadLocal(): SettingsPayload {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<SettingsPayload>) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SettingsPayload>(loadLocal);
  const pushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hydrate from the server once; server wins for cross-device consistency.
  useEffect(() => {
    let cancelled = false;
    api<SettingsPayload>('/settings')
      .then((server) => {
        if (!cancelled) setSettings(server);
      })
      .catch(() => {
        /* offline — keep local */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => applyTheme(settings.theme), [settings.theme]);

  const update = useMemo(
    () => (patch: Partial<SettingsPayload>) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        try {
          localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        if (pushTimer.current) clearTimeout(pushTimer.current);
        pushTimer.current = setTimeout(() => {
          void api('/settings', { method: 'PUT', body: next }).catch(() => {
            /* best-effort */
          });
        }, 500);
        return next;
      });
    },
    [],
  );

  return <Ctx.Provider value={{ settings, update }}>{children}</Ctx.Provider>;
}

export function useSettings(): SettingsCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSettings must be used within SettingsProvider');
  return ctx;
}
